/**
 * To add a provider: append its id here, add meta in RSS_DIRECTORY_PROVIDERS (name, logo),
 * add a logo under packages/shared/assets/img/rss-providers/, and seed catalog entries with feedUrl.
 */
export const RSS_DIRECTORY_PROVIDER_IDS = [
  'g1',
  'uol',
  'ebc',
  'folha',
  'cnn-brasil',
  'jornal-de-brasilia',
  'correio-braziliense',
  'r7',
  'ge',
  'canaltech',
  'valor'
] as const

export type TRssDirectoryProviderId = (typeof RSS_DIRECTORY_PROVIDER_IDS)[number]

export interface IRssDirectoryProviderMeta {
  id: TRssDirectoryProviderId
  /** Display name shown in picker UI and search tokens. */
  name: string
  /** Bundler-resolved asset path (app/player import the SVG from shared assets). */
  logoAssetPath: string
  /** @deprecated All providers now require hero photos at parse/filter time. */
  requiresItemImage?: boolean
  /** Future: player frame component id (v1 uses G1 story wall for all). */
  rendererId?: string
}

export const RSS_DIRECTORY_PROVIDERS: Record<TRssDirectoryProviderId, IRssDirectoryProviderMeta> = {
  'g1': {
    id: 'g1',
    name: 'G1',
    logoAssetPath: 'img/rss-providers/g1.svg',
    rendererId: 'g1-story-wall'
  },
  'uol': {
    id: 'uol',
    name: 'UOL',
    logoAssetPath: 'img/rss-providers/uol.svg',
    requiresItemImage: true,
    rendererId: 'g1-story-wall'
  },
  'ebc': {
    id: 'ebc',
    name: 'Agência Brasil (EBC)',
    logoAssetPath: 'img/rss-providers/ebc.svg',
    rendererId: 'g1-story-wall'
  },
  'folha': {
    id: 'folha',
    name: 'Folha de S.Paulo',
    logoAssetPath: 'img/rss-providers/folha.svg',
    rendererId: 'g1-story-wall'
  },
  'cnn-brasil': {
    id: 'cnn-brasil',
    name: 'CNN Brasil',
    logoAssetPath: 'img/rss-providers/cnn-brasil.svg',
    rendererId: 'g1-story-wall'
  },
  'jornal-de-brasilia': {
    id: 'jornal-de-brasilia',
    name: 'Jornal de Brasília',
    logoAssetPath: 'img/rss-providers/jornal-de-brasilia.svg',
    rendererId: 'g1-story-wall'
  },
  'correio-braziliense': {
    id: 'correio-braziliense',
    name: 'Correio Braziliense',
    logoAssetPath: 'img/rss-providers/correio-braziliense.svg',
    requiresItemImage: true,
    rendererId: 'g1-story-wall'
  },
  'r7': {
    id: 'r7',
    name: 'R7',
    logoAssetPath: 'img/rss-providers/r7.svg',
    rendererId: 'g1-story-wall'
  },
  'ge': {
    id: 'ge',
    name: 'ge',
    logoAssetPath: 'img/rss-providers/ge.svg',
    rendererId: 'g1-story-wall'
  },
  'canaltech': {
    id: 'canaltech',
    name: 'Canaltech',
    logoAssetPath: 'img/rss-providers/canaltech.svg',
    rendererId: 'g1-story-wall'
  },
  'valor': {
    id: 'valor',
    name: 'Valor Econômico',
    logoAssetPath: 'img/rss-providers/valor.svg',
    rendererId: 'g1-story-wall'
  }
}

export function isKnownRssDirectoryProvider(providerId: string): providerId is TRssDirectoryProviderId {
  return (RSS_DIRECTORY_PROVIDER_IDS as readonly string[]).includes(providerId)
}

export function getRssDirectoryProvider(
  providerId: string
): IRssDirectoryProviderMeta | undefined {
  if (providerId in RSS_DIRECTORY_PROVIDERS) {
    return RSS_DIRECTORY_PROVIDERS[providerId as TRssDirectoryProviderId]
  }
  return undefined
}

export function rssProviderRequiresItemImage(providerId: string): boolean {
  return getRssDirectoryProvider(providerId)?.requiresItemImage === true
}

export function rssDirectoryListingSearchHaystack(
  summary: {
    id: string
    label: string
    category: string
    provider: string
    layoutId: string
    layoutPreview: string
  },
  extraTokens: readonly string[] = []
): string {
  const providerMeta = getRssDirectoryProvider(summary.provider)
  return [
    summary.id,
    summary.label,
    summary.category,
    summary.provider,
    providerMeta?.name,
    summary.layoutId,
    summary.layoutPreview,
    ...extraTokens
  ]
    .filter((token): token is string => Boolean(token?.trim()))
    .join(' ')
    .toLowerCase()
}

export function rssDirectoryEntryMatchesSearchQuery(
  summary: {
    id: string
    label: string
    category: string
    provider: string
    layoutId: string
    layoutPreview: string
  },
  query: string,
  extraTokens: readonly string[] = []
): boolean {
  const q = query.trim().toLowerCase()
  if (!q) {
    return true
  }
  return rssDirectoryListingSearchHaystack(summary, extraTokens).includes(q)
}
